/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Heading,
  Icon,
  PageTabs,
  useToast,
} from "@elaniintc/goes-design-system";
import { ConfirmationModalBase } from "@/components/common/modals";
import { useMemo, useState, useEffect } from "react";
import { cn } from "@/utils/theme";
import TableHeaderWithTab from "@/components/common/table/header-with-tab";
import { DigitalResolutionTable } from "./components/resolution-table";
import { Link, useNavigate, useParams } from "react-router";
import dayjs from "dayjs";
import { DigitalRequestTable } from "./components/requests-table";
import { MoreDetailsModal } from "./components/MoreDetailsModal";
import { DetailsHistory } from "../components/DetailsHistory";
import { BadgesSection } from "./components/BadgesSection";
import {
  useDeleteRegistry,
  useGetRegistry,
} from "@/queries/digital-files/registries.api";
import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";
import type { RegistryType } from "@/schemas/digital-files/registry";
import { getDefaultDetailsFilters, getSectionsFromRegistry } from "../utils";
import { ResolutionModal } from "./components/ResolutionModal";
import { FiltersModal } from "@/components/ui/filters/FiltersModal";
import { type FilterOption } from "@/types/common/filters";
import { usePageSortFilters } from "@/hooks/use-page-fs";
import ComplexRelationships from "./components/complex-relationships";
import type { ProccesedExpedientValues } from "@/types/entities/templates";
import { Skeleton } from "@/components/ui/skeleton";
import { processValues } from "./utils";
import { buildHierarchyStatusString } from "@/pages/home/utils/serialization";
import { parseAsString, useQueryState } from "nuqs";

type TabPages = "details" | "history";
const pageTabs = [
  {
    label: "Detalle",
    key: "details",
  },
  // {
  //   label: "Historial",
  //   key: "history",
  // },
];

type TablePages = "requests" | "resolutions";
const tableTabs = [
  {
    label: "Solicitudes",
    value: "requests",
  },
  {
    label: "Resoluciones",
    value: "resolutions",
  },
];

const DetailsMenu = ({
  registry,
  entityId,
  onDelete,
}: {
  entityId?: string;
  registry?: RegistryType;
  onDelete: () => void;
}) => {
  const { hasPermission } = useAuthorization();
  const canUpdate = hasPermission(Permissions.expedient_base_registries.update);
  const canDelete = hasPermission(Permissions.expedient_base_registries.delete);

  if (!registry) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="quiet"
          size="icon"
          aria-label="Opciones"
          type="button"
          disabled={!canUpdate && !canDelete}
        >
          <Icon type="far" name="ellipsis-vertical" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className={cn(
          "flex flex-col text-body rounded-lg border-[0.5px] border-neutral bg-neutral-object font-normal shadow-neutral-xl",
        )}
        side="bottom"
        align="end"
      >
        {canUpdate && (
          <DropdownMenuItem
            className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
            asChild
          >
            <Link
              to={`/expedientes/entidad/${entityId}/registro/${registry.id}`}
            >
              Editar registro
            </Link>
          </DropdownMenuItem>
        )}

        {canUpdate && (
          <DropdownMenuItem
            className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
            onClick={onDelete}
          >
            Eliminar registro
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const RegistryDetailPage = () => {
  const { hasPermission } = useAuthorization();
  const [activeTab, setActiveTab] = useState<TabPages>("details");
  const [activeTableTab, setActiveTableTab] = useState<TablePages>("requests");
  const [modalState, setModalState] = useState<
    "idle" | "more-details" | "delete-registry" | "resolution" | "filters"
  >("idle");
  const [searchString, setSearchString] = useState("");
  const { registryId: id, id: entityId } = useParams();

  const { mutateAsync: deleteRegistry } = useDeleteRegistry(entityId);

  const canCreate = hasPermission(Permissions.expedient_base_registries.create); // manage canCreate permissions here
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [status, setStatus] = useQueryState(
    "status",
    parseAsString.withDefault(""),
  );

  const defaultFormatFilters = useMemo(
    () => getDefaultDetailsFilters(status, activeTableTab === "resolutions"),
    [status, activeTableTab],
  );

  const { formatFilters, setPage, setFormatFilters, query } =
    usePageSortFilters({
      defaultFormatFilters,
    });

  useEffect(() => {
    setFormatFilters(defaultFormatFilters);
  }, [defaultFormatFilters, setFormatFilters]);

  const { data, isLoading, error } = useGetRegistry(id || "", !!id);
  const isEmpty = !data || !!error;

  const sections = useMemo(() => {
    if (!data) return [];
    return getSectionsFromRegistry(data);
  }, [data]);

  const expirations = useMemo(() => {
    if (!data?.field_values) return {};
    const expirations: Record<string, string> = {};
    data.field_values.forEach((field) => {
      if (field.expiration_at) {
        expirations[field.expedient_base_entity_field_id] = field.expiration_at;
      }
    });
    return expirations;
  }, [data]);

  const [processedValues, setProcessedValues] =
    useState<ProccesedExpedientValues>({});
  const [isLoadingProcessedValues, setIsLoadingProcessedValues] =
    useState(false);

  useEffect(() => {
    const manageProcessedValues = async () => {
      setIsLoadingProcessedValues(true);
      const processedValues = await processValues(data);
      setProcessedValues(processedValues);
      setIsLoadingProcessedValues(false);
    };

    if (data) {
      manageProcessedValues();
    }
  }, [data]);

  const onCloseFilters = (type: string) => {
    if (type !== "clean") {
      setModalState("idle");
      return;
    }
    setFormatFilters(
      getDefaultDetailsFilters("", activeTableTab === "resolutions"),
    );
    setStatus("");
    setModalState("idle");
  };

  const onApplyFilters = (newFilters: FilterOption[]): FilterOption[] => {
    newFilters.forEach((f) => {
      if (f.isUpdated) {
        if (f.type === "hierarchyStatus") {
          setStatus(buildHierarchyStatusString(f as any));
        }

        setPage(1);
      }
    });

    // Set filter to updated false if result is empty
    const updatedFilters = newFilters.map((f) => {
      if (f.result === "") {
        return {
          ...f,
          isUpdated: false,
          result: "",
        };
      }
      return f;
    });
    return updatedFilters;
  };

  const handleDelete = () => {
    setModalState("delete-registry");
  };

  const confirmDelete = async () => {
    if (!id) return;
    try {
      await deleteRegistry(id);
      showToast({
        title: "Registro eliminado",
        description: "El registro ha sido eliminado correctamente.",
        options: {
          type: "success",
        },
      });
      navigate(`/expedientes`);
    } catch (e) {
      console.error("Error deleting registry:", e);
      showToast({
        title: "Error",
        description: "Hubo un error al eliminar el registro.",
        options: {
          type: "error",
        },
      });
    }
    setModalState("idle");
  };

  if (isLoading || !data) {
    return (
      <div className="w-full">
        <div className="flex flex-col gap-6 w-full min-w-0">
          <div className="flex items-center justify-center py-16">
            <div className="flex flex-col items-center gap-4">
              <Icon
                name="spinner-third"
                type="fas"
                className="animate-spin text-primary text-xl text-on-disabled"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <ConfirmationModalBase
        isOpen={modalState === "delete-registry"}
        onClose={() => setModalState("idle")}
        title="Eliminar registro"
        description="¿Está seguro que desea eliminar este registro? Esta acción no se puede deshacer."
        onAccept={confirmDelete}
        acceptText="Eliminar registro"
        cancelText="Cancelar"
        acceptVariant="danger-primary"
        isLoading={false}
      />
      <div className="flex flex-col gap-6">
        <div className="flex gap-2 items-center border-b border-neutral-silent">
          <Avatar shape="square" size="sm">
            <AvatarFallback
              shape="square"
              color={isEmpty ? "neutral" : "action"}
            >
              <Icon
                type="fas"
                name="folder"
                size="sm"
                className={cn(
                  "text-action-quiet",
                  isEmpty && "text-on-disabled",
                )}
              />
            </AvatarFallback>
          </Avatar>
          <Heading
            variant="h6"
            color="loud"
            className={cn("truncate", isEmpty && "text-on-disabled")}
          >
            {data?.name ?? ""}
          </Heading>
          <PageTabs
            tabs={pageTabs}
            activeKey={activeTab}
            className="justify-end flex-auto border-none"
            onTabChange={(key) => setActiveTab(key as TabPages)}
          />
          <DetailsMenu
            entityId={entityId}
            registry={data}
            onDelete={handleDelete}
          />
        </div>
        {activeTab === "details" && (
          <>
            <div>
              {sections && data?.field_values_formatted && (
                <>
                  {isLoadingProcessedValues ? (
                    <div className="flex gap-2 flex-wrap">
                      {Array.from({ length: 7 }).map((_, i) => (
                        <Skeleton key={`skeleton-${i}`} className="h-7 w-40" />
                      ))}
                    </div>
                  ) : (
                    <BadgesSection
                      sections={sections}
                      expirations={expirations}
                      processedValues={processedValues}
                      registry={data}
                      onOpenModal={() => setModalState("more-details")}
                    />
                  )}
                </>
              )}
              <div className="mt-2 flex gap-1 text-body-quiet text-size-body-sm justify-center sm:justify-start">
                <div className="flex gap-1">
                  {data?.unique_code && (
                    <div>
                      ID:{" "}
                      <span className="text-body-loud">
                        {data?.unique_code}
                      </span>{" "}
                      |
                    </div>
                  )}
                  {data?.associates && data?.associates?.length > 0 ? (
                    <div className="flex flex-col sm:flex-row justify-center sm:justify-start items-center gap-1">
                      <span>Personas asociadas:</span>

                      <div
                        className="flex relative items-center h-5"
                        style={{
                          width:
                            Math.min(data?.associates?.length, 3) * 10 +
                            18 +
                            "px",
                        }}
                      >
                        {Array.from({
                          length: Math.min(data?.associates?.length, 3),
                        }).map((_, index) => (
                          <Avatar
                            key={`avatar-${index}`}
                            size="2xs"
                            shape="circle"
                            className="absolute top-0"
                            style={{
                              left: 12 * index + "px",
                            }}
                          >
                            <AvatarFallback
                              className="bg-[#D8E6FD]"
                              shape="circle"
                            >
                              {data?.associates
                                ? data.associates[index].applicant?.first_name
                                    ?.charAt(0)
                                    .toUpperCase()
                                : "S"}
                            </AvatarFallback>
                          </Avatar>
                        ))}
                      </div>
                      <p className="text-action hidden md:block truncate">
                        {data?.associates?.length === 1
                          ? `${data?.associates?.[0]?.applicant?.first_name || ""} ${data?.associates?.[0]?.applicant?.last_name || ""}`
                          : "+" + data?.associates?.length + " usuarios"}
                      </p>
                    </div>
                  ) : (
                    <span>Sin personas asociadas</span>
                  )}
                  <span className="w-1 ml-2 border-l border-neutral-silent"></span>
                  {data?.related_registries &&
                  data?.related_registries?.length > 0 ? (
                    <div className="flex flex-col sm:flex-row justify-center sm:justify-start items-center gap-1">
                      <span>Registros asociados:</span>
                      <Badge size="sm">
                        {data?.related_registries?.length}
                        <Icon type="fas" name="folder" size="xs"></Icon>
                      </Badge>
                    </div>
                  ) : (
                    <span>Sin registros asociados</span>
                  )}
                  <span className="w-1 ml-2 border-l border-neutral-silent"></span>
                  {data?.updated_at ? (
                    <div className="flex gap-1 flex-col items-center sm:flex-row sm:items-start">
                      <span>Última actualización:</span>
                      <span className="capitalize">
                        {dayjs(data.updated_at).format("MMM DD, YYYY")}
                      </span>
                      {(data.created_by || data.updated_by) && (
                        <>
                          <span>por</span>
                          <span className="text-action">
                            {data.updated_by
                              ? data.updated_by?.name
                              : data.created_by?.name}
                          </span>
                        </>
                      )}
                    </div>
                  ) : (
                    <span>Sin actualizaciones</span>
                  )}
                </div>
              </div>
              <div className="mt-2">
                {
                  <ComplexRelationships
                    expedient_base_entity_id={
                      data?.expedient_base_entity_id || ""
                    }
                    expedient_base_registry_id={data?.id || ""}
                    shouldFetch={true}
                  />
                }
                {/* TODO: condicionar y mostarr solo si hay relaciones complejas */}
              </div>
            </div>
            <div>
              <div className="h-full flex flex-col bg-neutral-object border-neutral border-[0.5px] rounded-xl shadow-neutral-md overflow-hidden min-h-[calc(100vh-345px)]">
                <TableHeaderWithTab
                  tabs={tableTabs}
                  activeTab={activeTableTab}
                  onTabChange={(tab) => {
                    setFormatFilters(
                      getDefaultDetailsFilters(
                        activeTableTab === "resolutions" ? "" : status,
                        activeTableTab !== "resolutions",
                      ),
                    );
                    setSearchString("");
                    setActiveTableTab(tab as TablePages);
                    setStatus("");
                  }}
                  placeholder={
                    activeTableTab === "requests"
                      ? "Buscar solicitud..."
                      : "Buscar resolución..."
                  }
                  canCreate={activeTableTab === "resolutions" && canCreate}
                  createLabel="Agregar Resolución"
                  filtersApplied={
                    formatFilters.filter((f) => f.isUpdated).length
                  }
                  onFilter={() => {
                    setModalState("filters");
                  }}
                  search={searchString}
                  setSearch={(value) => setSearchString(value ?? "")}
                  onCreate={() => setModalState("resolution")}
                />
                {activeTableTab === "requests" ? (
                  <DigitalRequestTable
                    requestList={data?.formality_requests || []}
                    searchString={searchString}
                    queryFilters={{ status, ...query.filter }}
                  />
                ) : (
                  <DigitalResolutionTable
                    registryId={id || ""}
                    searchString={searchString}
                    queryFilters={query.filter}
                  />
                )}
              </div>
            </div>
          </>
        )}
        {activeTab === "history" && id && (
          <DetailsHistory entityId={id} entityType="registry" />
        )}
        {data && (
          <MoreDetailsModal
            entityId={entityId}
            registry={data}
            sections={sections}
            expirations={expirations}
            processedValues={processedValues ?? {}}
            isOpen={modalState === "more-details"}
            onClose={() => setModalState("idle")}
          />
        )}
        <ResolutionModal
          isOpen={modalState === "resolution"}
          onClose={() => setModalState("idle")}
        />
      </div>
      {modalState === "filters" && (
        <FiltersModal
          isOpen={modalState === "filters"}
          onToggle={onCloseFilters}
          hasCurrentFilters={formatFilters.some((filter) => filter.isUpdated)}
          items={formatFilters}
          onApply={(newFilters) => {
            const filters = onApplyFilters(newFilters);
            setFormatFilters(filters);
            setModalState("idle");
          }}
        />
      )}
    </>
  );
};
export { RegistryDetailPage };
